/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { useCallback, useEffect, useRef, useState } from 'react';

/** Like useState, but saves in localStorage.
 *  For they key, please use a standard like:  docsindex.columnOrder or topMenu.showSearchBar
 *
 *  Usage:
 *   const [value, setValue] = useStateLocalStorage('myKey.foo', 'defaultValue');
 *
 * The initial value can also be a function, like in useState, for example:
 *  const [value, setValue] = useStateLocalStorage('myKey.foo', () => {
 *    // some expensive calculation to get the initial value
 *   return 'defaultValue';
 *  });
 */
export const useStateLocalStorage = <T>(
  key: string,
  initialValue: T | (() => T)
): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const [value, setValue] = useState<T>(() => {
    try {
      const item = localStorage.getItem(key);
      if (item !== null) return JSON.parse(item) as T;
    } catch (error) {}
    return typeof initialValue === 'function' ? (initialValue as () => T)() : initialValue;
  });

  const setValueAndStore = useCallback(
    (newValue: React.SetStateAction<T>) => {
      setValue((value) => {
        // if newValue is function, call it with current value
        const valueToStore = newValue instanceof Function ? newValue(value) : newValue;
        // Now store in localStorage
        localStorage.setItem(key, JSON.stringify(valueToStore));
        return valueToStore;
      });
    },
    [key]
  );
  return [value, setValueAndStore];
};

/** Returns whether this component is mounted or not
 *  Useful if using setTimeout or other async functions
 *  Use like this:
 *
 *  ```javascript
 *      const isMounted = useIsMounted();
 *      setTimeout(() => {
 *       if (!isMounted.current) return;
 *       // do something
 *      }, 1000);
 * ```
 *
 */
export const useIsMounted = () => {
  const isMounted = useRef(false);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  return isMounted;
};
